import { useState } from "react";
import axios, { AxiosResponse, AxiosError } from "axios";
import HttpStatus from "http-status-codes";
import { AuthorizationToken } from "helpers/AuthorizationToken";
import { isValidNumber, SrmBoolean, getApiErrorMsg } from "helpers/helper";
import reduxStore from "reduxstore/reduxStore";
import {
  setCurrentUser,
  generateOldPermissionData,
  generateNewPermissionData
} from "actions/authActions";
import {
  AUTH_URL,
  COMPANY_PERMISSION_URL,
  USER_URL,
  USER_PERMISSION_URL,
  USER_INSTANCE_URL,
  OPERATOR_URL,
  COMPANY_UPGRADED_ERROR,
  TIS_SESSION_VERIFY_URL
} from "constants/AppConstants";
import i18n from "i18n/i18n";
import { get, pick } from "lodash";
import { Alert } from "@texada/ui";

enum APPLICATION_ID {
  SRM = 100026,
  TW = 100027,
  GW = 100029
}

export enum LoginSource {
  SRM = "SRM",
  TW = "TW",
  TM = "TM"
}

export enum LoginTenant {
  LIVE = "Live",
  PLAY = "Play"
}

export interface LoginData {
  email: string;
  password: string;
  company_id?: number;
  loginSource?: string;
}

interface MobileLoginData {
  mobile_session_token: string;
}

interface LoginResponse {
  token: string;
  flask_token: string;
  flask_url: string;
  gateway_token: string;
  gateway_url: string;
}

export interface LoginFullResponse extends LoginResponse {
  user: any;
  is_operator?: boolean;
  use_texada_pay?: boolean;
  comp_perms: any;
  permissions: any;
  locations: any[];
  divisions: any[];
  upgraded?: boolean;
  companyId?: number;
  isSuperAdmin?: boolean;
  company_sso_enabled?: boolean;
  company_features?: any;
}

interface SetTokenParam {
  token: string;
  flask_token: string;
  flask_url: string;
  authRefreshToken?: string;
  gateway_token: string;
  gateway_url: string;
}

function isMobileLoginData(
  options: LoginData | MobileLoginData
): options is MobileLoginData {
  return options.hasOwnProperty("mobile_session_token");
}

function isFullResponse(
  response: AxiosResponse<LoginResponse | LoginFullResponse>
): response is AxiosResponse<LoginFullResponse> {
  return response.status === HttpStatus.OK;
}

type UserResponse = Omit<LoginFullResponse, keyof LoginResponse>;

export async function setTokens({
  token,
  flask_token,
  flask_url,
  gateway_token,
  gateway_url
}: SetTokenParam) {
  localStorage.setItem("flaskToken", flask_token);
  localStorage.setItem("flask_url", flask_url);
  localStorage.setItem("gatewayToken", gateway_token);
  localStorage.setItem("gateway_url", gateway_url);
  if (token) {
    localStorage.setItem("jwtToken", token);
  }
}

export async function setUser(loginResponse: LoginFullResponse) {
  const user = {
    ...loginResponse.user,
    compperms: loginResponse.comp_perms,
    companyFeatures: loginResponse.company_features,
    locations: loginResponse.locations,
    divisions: loginResponse.divisions,
    useTexadaPay: loginResponse.use_texada_pay,
    upgraded: loginResponse.upgraded,
    companyId: loginResponse.companyId,
    permissions: loginResponse.permissions,
    company_sso_enabled:
      loginResponse.company_sso_enabled ||
      loginResponse.user.company_sso_enabled,
    isSuperAdmin: false
  };

  if (loginResponse.isSuperAdmin) {
    user.isSuperAdmin = loginResponse.isSuperAdmin;
  }
  localStorage.setItem("isOperator", JSON.stringify(loginResponse.is_operator));
  localStorage.setItem("currentUser", JSON.stringify(user));
  await reduxStore.dispatch(setCurrentUser(user));
  return user;
}

function pollQueueAPI(url): Promise<AxiosResponse<UserResponse>> {
  const timeInterval = 1000;
  const timeout = 3 * 60 * 1000;
  const maxIterations = timeout / timeInterval;
  let iteration = 0;

  async function makeRequest(resolve, reject) {
    const response: AxiosResponse<never | UserResponse> = await axios
      .get(url)
      .catch(error => {
        if (!error.hasOwnProperty("response"))
          reject(
            i18n.t(
              "connection_error_please_try_again_later_or_contact_texada_software"
            )
          );
        return error.response;
      });

    if (!response.request.responseURL.includes(url)) {
      resolve(response);
    } else if (response.status === 200) {
      const shouldTimeout = iteration++ >= maxIterations;
      if (shouldTimeout)
        reject(
          i18n.t(
            "connection_error_please_try_again_later_or_contact_texada_software"
          )
        );
      else setTimeout(() => makeRequest(resolve, reject), timeInterval);
    } else {
      reject(response);
    }
  }

  return new Promise(makeRequest);
}

export type LoginFunc = (
  postData: LoginData | MobileLoginData
) => Promise<void>;

export function useLogin(
  setIsSyncingData: React.Dispatch<React.SetStateAction<boolean>>
) {
  const [error, setError] = useState<AxiosError>();

  const login: LoginFunc = async ({
    ...postData
  }: LoginData | MobileLoginData) => {
    setError(undefined);
    if (!isMobileLoginData(postData)) {
      postData.email = postData.email.trim();
    }

    try {
      const response = await axios.post<LoginResponse | LoginFullResponse>(
        AUTH_URL,
        postData
      );
      if (response?.data?.message === COMPANY_UPGRADED_ERROR) {
        window.location.href = `/company/${response?.data?.company}`;
        return false;
      }
      await setTokens(response.data);
      AuthorizationToken.set(response.data.token);

      if (isFullResponse(response)) {
        if (response.data.upgraded) {
          await handleNewLoginResponse(response);
        } else {
          await handleOldLoginResponse(response);
        }
      } else {
        setIsSyncingData(true);
        const { location: queueUrl } = response.headers;
        const userResponse = await pollQueueAPI(queueUrl);
        if (userResponse.data.upgraded) {
          await handleNewLoginResponse(userResponse);
        } else {
          await handleOldLoginResponse(userResponse);
        }
      }
    } catch (error) {
      setIsSyncingData(false);
      setError(error);
      throw error;
    }
  };

  const loginAuth = async (
    companyId,
    showDivisionModal = undefined,
    tisSession = undefined,
    CompanySsoEnabled = false
  ) => {
    setError(undefined);

    try {
      const res = await axios.post(`${TIS_SESSION_VERIFY_URL}/${companyId}`, {
        tis_session: tisSession,
        company_sso_enabled: CompanySsoEnabled
      });
      await setTokens(res.data);
      AuthorizationToken.set(res.data.token);
      if (res.data.company.upgraded) {
        return await handleNewLoginResponse(
          res,
          showDivisionModal,
          CompanySsoEnabled
        );
      } else {
        localStorage.setItem("currentTenant", companyId);
        return await handleOldLoginResponse(
          res,
          showDivisionModal,
          CompanySsoEnabled
        );
      }
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  return { login, loginAuth, error, setError };
}

export async function openSrmManagement(
  upgraded = false,
  divisionSrmUid = undefined
) {
  const url =
    upgraded && divisionSrmUid !== undefined
      ? `/rest-api/srmlogin?division=${encodeURIComponent(divisionSrmUid)}`
      : "/rest-api/srmlogin";
  try {
    const loginURL = await axios.get(url);
    window.location.href = loginURL.data;
  } catch {
    Alert.error({
      message: i18n.t("could_not_login_to_srm")
    });
  }
}

export const handleOldLoginResponse = async (
  response,
  showDivisionModal = undefined,
  CompanySsoEnabled = false
) => {
  const { user, comp_perms } = response.data;
  const permissions = generateOldPermissionData(
    comp_perms,
    response.data.divisions,
    response.data.locations
  );

  if (showDivisionModal && comp_perms.operator_by_division) {
    showDivisionModal(true);
  }

  let userInfo = {
    ...response.data,
    company_sso_enabled: CompanySsoEnabled,
    isSuperAdmin: response.data.user.is_super_admin,
    srm_uid: response.data.user.srm_uid,
    permissions: {
      ...permissions.permissions,
      admin: user.settings_menu_access,
      update_settings: user.settings_menu_access,
      capture_signatures: user.capture_signatures,
      change_asset_status: user.change_asset_status,
      maintain_sites: user.maintain_sites,
      update_meter_reading: user.update_meter_reading,
      view_inventory: user.view_inventory,
      workorder_default_reporting: user.workorder_default_reporting
    },
    locations: permissions.locations,
    divisions: permissions.divisions,
    companyId: response.data.user.company
  };

  const userInfoWithDocAccess = await setDocDivisionAccess(userInfo);
  await setUser(userInfoWithDocAccess);

  if (showDivisionModal && !comp_perms.operator_by_division) {
    return true;
  }
  return false;
};

export const handleNewLoginResponse = async (
  response,
  showDivisionModal = undefined,
  CompanySsoEnabled = false
) => {
  const companyPermissionRes = await axios.get(
    `${COMPANY_PERMISSION_URL}${response.data.user.company}`
  );
  const userRes = await axios.get(
    `${USER_URL}${response.data.user.id}?include_flask=true`
  );
  const permissionRes = await axios.get(
    `${USER_PERMISSION_URL}${response.data.user.id}`
  );
  const permissions = generateNewPermissionData(
    companyPermissionRes.data,
    permissionRes.data.divisions,
    permissionRes.data.locations
  );

  if (showDivisionModal && companyPermissionRes.data.operator_by_division) {
    showDivisionModal(true);
  }

  let userInfo = {
    ...response.data,
    user: userRes.data,
    srm_uid: userRes.data.srm_uid,
    company_sso_enabled: CompanySsoEnabled,
    // TODO: remove compperms
    comp_perms: companyPermissionRes.data,
    permissions: permissions.permissions,
    locations: permissions.locations,
    divisions: permissions.divisions,
    useTexadaPay: companyPermissionRes.data.use_texada_pay,
    companyId: response.data.user.company,
    upgraded: companyPermissionRes.data.upgraded,
    isSuperAdmin: permissionRes.data.is_super_admin,
    is_operator: userRes.data.is_support
  };

  const userInfoWithDocAccess = await setDocDivisionAccess(userInfo);
  await setUser(userInfoWithDocAccess);

  if (showDivisionModal && !companyPermissionRes.data.operator_by_division) {
    return true;
  }
  return false;
};

export function getLoginError(error: AxiosError): string {
  if (!error || isSCSGeneratePassword(error)) {
    return "";
  }

  if (error?.response?.data?.message) {
    if (get(error, "response.status_code", "") === HttpStatus.NOT_ACCEPTABLE) {
      return i18n.t(
        "there_is_a_new_app_version_available_please_refresh_page_to_continue"
      );
    }
    return error.response.data.message;
  }

  if (error?.response?.data?.form) {
    return error.response.data.form;
  }

  if (error?.message && !error?.response) {
    return error.message;
  }

  return (
    getApiErrorMsg(error) ||
    i18n.t("something_went_wrong_please_contact_texada_software")
  );
}

export async function switchInstance(userId, currentCompanyId, companyId) {
  if (!currentCompanyId || !companyId || currentCompanyId === companyId)
    return false;

  if (!isValidNumber(companyId) || !isValidNumber(currentCompanyId))
    return false;

  try {
    const res = await axios.patch(`${USER_INSTANCE_URL}/${userId}`, {
      company_id: companyId
    });

    if (!res?.data) {
      return false;
    }
    return true;
  } catch (err) {
    console.log(err);
  }

  return false;
}

export function isSCSGeneratePassword(error: AxiosError): boolean {
  return (
    error?.response?.status === HttpStatus.FORBIDDEN &&
    error?.response?.data?.message
  );
}

export async function setDocDivisionAccess(user, divisionSrmUid = "") {
  try {
    const res = await axios.get(
      `${OPERATOR_URL}/${user.srm_uid}/${encodeURIComponent(divisionSrmUid)}`,
      {
        responseType: "json"
      }
    );

    let newUserWithDocDivision = { ...user };
    const docDivisionAccess = [];
    const docLocationAccess = [];
    const resArray =
      res?.data?.operators?.[0]?.divisions?.length > 0
        ? res.data.operators[0].divisions
        : [];

    resArray.forEach(division => {
      const userDivisionIndex = newUserWithDocDivision.divisions.findIndex(
        e => e.warehouse.srm_uid === division.division.trim()
      );
      const userLocationIndex = newUserWithDocDivision.locations.findIndex(
        e => e.warehouse.srm_uid === division.location.trim()
      );

      if (userDivisionIndex > -1) {
        newUserWithDocDivision.divisions[userDivisionIndex].prompt_division =
          division.promptForDiv === SrmBoolean.True ||
          newUserWithDocDivision.divisions[userDivisionIndex].prompt_division;
        newUserWithDocDivision.divisions[userDivisionIndex].is_doc_division =
          true;

        const docDivisionIndex = docDivisionAccess.findIndex(
          e =>
            e.srm_uid ===
            newUserWithDocDivision.divisions[userDivisionIndex].warehouse
              .srm_uid
        );

        if (docDivisionIndex > -1) {
          const docDivision = docDivisionAccess[docDivisionIndex];
          docDivisionAccess[docDivisionIndex] = {
            ...docDivision,
            prompt_division:
              newUserWithDocDivision.divisions[userDivisionIndex]
                .prompt_division || docDivision.prompt_division,
            is_doc_division:
              newUserWithDocDivision.divisions[userDivisionIndex]
                .is_doc_division || docDivision.is_doc_division
          };
        } else {
          docDivisionAccess.push({
            srm_uid:
              newUserWithDocDivision.divisions[userDivisionIndex].warehouse
                .srm_uid,
            ...pick(newUserWithDocDivision.divisions[userDivisionIndex], [
              "prompt_division",
              "is_doc_division"
            ])
          });
        }
      }

      if (userLocationIndex > -1) {
        newUserWithDocDivision.locations[userLocationIndex].prompt_location =
          division.promptForLoc === SrmBoolean.Division
            ? division?.promptForLoc
            : division.promptForLoc === SrmBoolean.True;
        newUserWithDocDivision.locations[userLocationIndex].is_doc_location =
          true;

        const docLocationIndex = docLocationAccess.findIndex(
          e =>
            e.srm_uid ===
            newUserWithDocDivision.locations[userLocationIndex].warehouse
              .srm_uid
        );

        if (docLocationIndex > -1) {
          const docLocation = docLocationAccess[docLocationIndex];
          docLocationAccess[docLocationIndex] = {
            ...docLocation,
            prompt_location:
              newUserWithDocDivision.locations[userLocationIndex]
                .prompt_location || docLocation.prompt_location,
            is_doc_location:
              newUserWithDocDivision.locations[userLocationIndex]
                .is_doc_location || docLocation.is_doc_location
          };
        } else {
          docLocationAccess.push({
            srm_uid:
              newUserWithDocDivision.locations[userLocationIndex].warehouse
                .srm_uid,
            ...pick(newUserWithDocDivision.locations[userLocationIndex], [
              "prompt_location",
              "is_doc_location"
            ])
          });
        }
      }
    });

    const preSelected =
      JSON.parse(localStorage.getItem("docDivisionAccess")) || {};
    localStorage.setItem(
      "docDivisionAccess",
      JSON.stringify({
        ...preSelected,
        [user.companyId]: {
          divisions: docDivisionAccess,
          locations: docLocationAccess
        }
      })
    );
    return newUserWithDocDivision;
  } catch (err) {
    console.debug(err);
    return user;
  }
}

export function getLoginSource(source) {
  if (source === "mobile") {
    return LoginSource.TM;
  }

  return LoginSource.TW;
}

export const getSelectedLoginTo = applicationId => {
  if (Number(applicationId) === APPLICATION_ID.SRM) return LoginSource.SRM;
  return LoginSource.TW;
};
